import { Fragment, useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  BarChart3,
  BookOpenText,
  Bot,
  Building2,
  Database,
  Toolbox,
  FolderKanban,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  Moon,
  Plug,
  ScrollText,
  Sun,
  Users,
  UserCog,
  X,
  type IconComponent,
} from '@/components/icons';
import { ROLE_LABELS } from '@1qa/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { buildNav, type NavIcon } from '@/lib/permissions';
import { useTheme } from '@/lib/theme';
import { onRecorderScriptSaved, onRecorderStopped, onRecorderTestSaved } from '@/lib/recorderBridge';
import { useToast } from '@/components/ui';
import { SidebarProjectTree } from '@/components/ProjectTree';
import { Logo } from './Logo';
import { OrgSwitcher } from './OrgSwitcher';

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const ICONS: Record<NavIcon, IconComponent> = {
  dashboard: LayoutDashboard,
  projects: FolderKanban,
  reports: BarChart3,
  members: Users,
  orgs: Building2,
  users: UserCog,
  invitations: Mail,
  plugins: Plug,
  ai: Bot,
  testdata: Database,
  audit: ScrollText,
  guide: BookOpenText,
  helpers: Toolbox,
};

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { user, currentOrgRole, currentOrgId } = useAuth();
  const nav = buildNav(user?.platformRole ?? 'USER', currentOrgRole);
  const main = nav.filter((n) => n.section === 'main');
  const settings = nav.filter((n) => n.section === 'settings');
  const admin = nav.filter((n) => n.section === 'admin');
  const help = nav.filter((n) => n.section === 'help');
  // New report comments addressed to me (as viewer or as the member who shared).
  // Admins are not part of any organization: no org switcher, no report comments.
  const isAdmin = user?.platformRole === 'ADMIN';
  const unread = useQuery({ queryKey: ['comments-unread'], queryFn: api.unreadComments, enabled: !!user && !isAdmin, refetchInterval: 60_000 });
  const unreadCount = unread.data?.total ?? 0;
  // Same query (and cache) as the Dashboard and Test projects pages; feeds the project tree under "Test projects".
  const showTree = !isAdmin && main.some((n) => n.to === '/projects');
  const projects = useQuery({ queryKey: ['projects', currentOrgId], queryFn: () => api.projects(currentOrgId!), enabled: showTree && !!currentOrgId });
  const roleLabel = user && user.platformRole !== 'USER' ? ROLE_LABELS[user.platformRole] : currentOrgRole ? ROLE_LABELS[currentOrgRole] : 'User';

  const link = (item: (typeof nav)[number]) => {
    const Icon = ICONS[item.icon];
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.to === '/'}
        onClick={onNavigate}
        className={({ isActive }) =>
          clsx(
            // Figma UI kit, Sidebar Nav (node 1:1694): 3px accent bar + #181818 on the active item.
            'group flex items-center gap-3 border-l-3 px-5 py-3 text-xs font-semibold tracking-[0.05em] uppercase transition',
            isActive ? 'border-primary bg-surface text-primary' : 'border-transparent text-fg-secondary hover:bg-surface hover:text-fg',
          )
        }
      >
        {() => (
          <>
            <span className="flex w-4 shrink-0 justify-center">
              <Icon className="size-3.5" />
            </span>
            {item.label}
            {item.to === '/reports' && unreadCount > 0 && (
              <span
                className="ml-auto rounded-full bg-primary px-2 py-0.5 text-[10px]/[15px] font-semibold tracking-normal text-primary-fg tabular-nums"
                aria-label={`${unreadCount} new comment${unreadCount === 1 ? '' : 's'}`}
              >
                {unreadCount}
              </span>
            )}
          </>
        )}
      </NavLink>
    );
  };

  return (
    <div className="flex h-full flex-col border-r border-border bg-surface-sunken">
      <div className="flex items-center border-b border-border p-5">
        <Logo className="text-lg!" />
      </div>
      <div className="border-b border-border p-4">
        {isAdmin ? (
          <div className="rounded-field bg-surface px-3 py-2.5 text-xs text-fg-muted ring-1 ring-border ring-inset">
            <span className="block text-sm font-medium text-fg">Platform administration</span>
            Users, Project Admins and audit
          </div>
        ) : (
          <OrgSwitcher />
        )}
      </div>
      <nav className="flex flex-1 flex-col overflow-y-auto py-2" aria-label="Main">
        <div>
          {main.map((item) =>
            item.to === '/projects' && showTree && projects.data ? (
              <Fragment key={item.to}>
                {link(item)}
                <SidebarProjectTree projects={projects.data} onNavigate={onNavigate} />
              </Fragment>
            ) : (
              link(item)
            ),
          )}
        </div>
        {settings.length > 0 && (
          <div>
            <div className="mx-5 my-2 h-px bg-border" />
            <div className="px-5 pt-2 pb-1 text-[11px] font-semibold tracking-label text-fg-muted uppercase">Organization settings</div>
            {settings.map(link)}
          </div>
        )}
        {admin.length > 0 && (
          <div>
            <div className="mx-5 my-2 h-px bg-border" />
            <div className="px-5 pt-2 pb-1 text-[11px] font-semibold tracking-label text-fg-muted uppercase">Administration</div>
            {admin.map(link)}
          </div>
        )}
        {help.length > 0 && (
          <div className="mt-auto">
            <div className="mx-5 my-2 h-px bg-border" />
            {help.map(link)}
          </div>
        )}
      </nav>
      {user && (
        <div className="flex items-center gap-3 border-t border-border px-5 py-4">
          <span className="grid size-7 shrink-0 place-items-center rounded-full bg-primary text-[11px] font-semibold text-primary-fg" aria-hidden>
            {initialsOf(user.name)}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-xs font-semibold text-fg">{user.name}</span>
            <span className="block truncate text-xs text-fg-muted">{roleLabel}</span>
          </span>
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const { user, currentOrgRole, signOut } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  if (!user) return null;
  const initials = initialsOf(user.name);
  const role = user.platformRole !== 'USER' ? ROLE_LABELS[user.platformRole] : currentOrgRole ? ROLE_LABELS[currentOrgRole] : 'User';

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={toggle}
        className="grid place-items-center rounded-full text-fg ring-1 ring-border ring-inset transition size-11 hover:bg-fg/8"
        aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {theme === 'dark' ? <Sun className="size-5" /> : <Moon className="size-5" />}
      </button>
      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={open}
          className="flex items-center gap-3 rounded-full py-1.5 pr-4 pl-1.5 transition hover:bg-fg/8"
        >
          <span className="grid size-8 place-items-center rounded-full bg-primary text-xs font-semibold text-primary-fg">{initials}</span>
          <span className="hidden text-left sm:block">
            <span className="block text-sm leading-tight font-medium text-fg">{user.name}</span>
            <span className="block text-xs leading-tight text-fg-muted">{role}</span>
          </span>
        </button>
        {open && (
          <div
            role="menu"
            className="absolute right-0 z-40 mt-2 w-60 animate-slide-up rounded-card bg-surface p-1.5 ring-1 ring-border ring-inset"
          >
            <div className="border-b border-border-subtle px-3 py-2">
              <div className="truncate text-sm font-medium text-fg">{user.name}</div>
              <div className="truncate text-xs text-fg-muted">{user.email}</div>
            </div>
            <button
              role="menuitem"
              type="button"
              onClick={() => {
                signOut();
                navigate('/login', { replace: true });
              }}
              className="mt-1 flex w-full items-center gap-2 rounded-field px-3 py-2 text-sm text-danger-text hover:bg-danger-subtle"
            >
              <LogOut className="size-4" /> Sign out
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const qc = useQueryClient();
  const toast = useToast();

  useEffect(() => setMobileOpen(false), [location.pathname]);

  // The recorder saves tests straight to the API; refresh the project's tests when it says so.
  useEffect(
    () =>
      onRecorderTestSaved(({ projectId, name }) => {
        qc.invalidateQueries({ queryKey: ['tests', projectId] });
        qc.invalidateQueries({ queryKey: ['project', projectId] });
        toast.success(`“${name}” was saved from the recorder`);
      }),
    [qc, toast],
  );
  useEffect(
    () =>
      onRecorderScriptSaved(({ projectId, name }) => {
        qc.invalidateQueries({ queryKey: ['perf-scripts', projectId] });
        toast.success(`The performance script “${name}” was saved from the recorder`);
      }),
    [qc, toast],
  );
  useEffect(() => onRecorderStopped(() => toast.info('Recording stopped. Click the 1QA Recorder icon in your browser toolbar to review and save the test.')), [toast]);

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[240px_1fr] print:block">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded-field focus:bg-surface focus:px-4 focus:py-2 focus:text-fg focus:ring-1 focus:ring-border"
      >
        Skip to content
      </a>

      <aside className="no-print sticky top-0 hidden h-dvh lg:block">
        <Sidebar />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
          <div className="absolute inset-0 animate-fade-in bg-background/70 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-60 animate-slide-up">
            <Sidebar onNavigate={() => setMobileOpen(false)} />
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="absolute top-5 right-3 rounded-full p-1.5 text-fg-muted hover:text-fg"
              aria-label="Close navigation"
            >
              <X className="size-5" />
            </button>
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-col">
        <header className="no-print sticky top-0 z-30 flex h-16 items-center justify-between gap-4 border-b border-border bg-background/85 px-4 backdrop-blur-lg sm:px-8">
          <button
            type="button"
            className="grid place-items-center rounded-full text-fg ring-1 ring-border ring-inset transition size-11 hover:bg-fg/8 lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
          >
            <Menu className="size-5" />
          </button>
          <div className="lg:hidden">
            <Logo withText={false} />
          </div>
          <div className="flex-1" />
          <UserMenu />
        </header>
        <main id="main" className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6 lg:px-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
